import { SolomonLogo, useOrgAuth, useOrgAuthStore } from "@solomon-apps/shared";
import { ResultPanel } from "@solomon-apps/shared/src/components/console";
import {
    Alert,
    AlertDescription,
    AlertTitle
} from "@solomon-apps/shared/src/components/ui/alert";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { linkVariants } from "@solomon-apps/shared/src/components/ui/link";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    ArrowRightLeftLongIcon,
    ArrowRightLongIcon,
    CheckIcon
} from "@solomon-labs/design/components/icons";
import {
    type ReactElement,
    type ReactNode,
    useCallback,
    useEffect
} from "react";
import { useNavigate } from "react-router-dom";

import { CLIENT_HOME_PATH } from "@/constants/navigation";
import { CLIENT_OAUTH_CONFIG } from "@/constants/oauthConfig";
import { useClientPlatformAccess } from "@/hooks/useClientPlatformAccess";

import type { OnboardingScenario } from "./types";

/**
 * Reusable page chrome for the onboarding hub.
 *
 * Wraps: app header, the turn-taking banner, and the decline alert. The
 * hub layout is injected via the `hub` slot.
 *
 * For the "Approved" application state the hub renders the celebration
 * card in its right pane.
 */
export function OnboardingShell({
    scenario,
    hub,
    celebrating = false
}: {
    scenario: OnboardingScenario;
    hub: ReactNode;
    /** The one-time approval celebration is on screen. While it is, the
     *  approved banner stays quiet: the card says the same thing with more
     *  ceremony and owns the way into the console. */
    celebrating?: boolean;
}): ReactElement {
    return (
        <main className="min-h-dvh bg-surface-ground">
            {/* Outer wrapper has only vertical breathing room and the
                max-width clamp — horizontal padding lives on each child
                section so the section dividers (header underline, vertical
                column rule) extend edge-to-edge of the max-width
                without being inset by a container's padding. No
                `space-y-*` between sections either: the hub's vertical
                divider needs to butt directly against the header's bottom
                border, and any inter-section gap is owned by each
                section's own top padding instead. */}
            <div className="max-w-3xl mx-auto pt-10 pb-50 lg:max-w-7xl">
                <ApplicationHeader scenario={scenario} />

                {scenario.state === "Declined" && (
                    <section className="pt-6">
                        <Alert variant="destructive" className="px-5 py-4">
                            <AlertTitle>Application Denied</AlertTitle>
                            <AlertDescription>
                                We&apos;re unable to approve your application at
                                this time. If you think this was made in error
                                please reach out to{" "}
                                {/* TODO: replace with the issuer's
                                    `org.clientContactEmail` (defaults to
                                    primary admin's email) once the issuer
                                    profile setting is wired through the
                                    /me response. See yaas-issuer intents
                                    for the setting's lifecycle. */}
                                <a
                                    href="mailto:compliance@solomonlabs.org"
                                    className={linkVariants()}
                                >
                                    compliance@solomonlabs.org
                                </a>
                                .
                            </AlertDescription>
                        </Alert>
                    </section>
                )}

                {/* The turn-taking line, above the hub: whose move it is, and
                    what happens next. Submit lives inside the hub now, on the
                    Review and submit section, so the page no longer ends on a
                    footer action detached from what it files. */}
                <ApplicationBanner
                    banner={scenario.banner}
                    celebrating={celebrating}
                />

                {hub}
            </div>
        </main>
    );
}

// ─── ApplicationHeader ───────────────────────────────────────────────────────

function ApplicationHeader({
    scenario
}: {
    scenario: OnboardingScenario;
}): ReactElement {
    const me = useOrgAuthStore((s) => s.me);
    const { logout } = useOrgAuth(CLIENT_OAUTH_CONFIG);
    const userName = me?.user.fullName ?? me?.user.email ?? null;
    // Prefer the org's legal name (populated since migration
    // 1765205200050; surfaced via `/me` as `org.legalName`). Fall back to
    // the display name, then to the mock-scenario brand for the offline
    // / pre-auth render path.
    const headingOrgName =
        me?.org.legalName?.trim() || me?.org.name?.trim() || scenario.orgName;

    return (
        <header className="pt-2 pb-6 border-b border-line-base flex flex-col gap-2">
            {/* Row 1 — title and brand mark. The mark stacks above the title
                on mobile (`flex-col-reverse`, since Heading emits `actions`
                after the title) and sits beside it from `md` up. */}
            <Heading
                title={`${headingOrgName} Onboarding`}
                className="flex flex-col-reverse items-start gap-3 md:flex-row md:items-center md:gap-4"
                actions={
                    <SolomonLogo
                        className="h-6 w-auto text-foreground-base md:h-10"
                        title="Solomon"
                    />
                }
            />
            {/* Row 2 — the "who am I / switch" affordance, full width under
                the title row rather than inside Heading's title stack. */}
            {userName && (
                <Text variant="small" className="text-foreground-muted">
                    Not {userName}?{" "}
                    <Button
                        type="button"
                        variant="transparent"
                        interaction="underline"
                        size="fit"
                        /* `leading` (not a child) so the icon sits outside the
                           button-label span and the `gap` actually applies. */
                        leading={<ArrowRightLeftLongIcon className="size-4" />}
                        gap="1"
                        onClick={() => void logout()}
                        className="text-foreground-base hover:text-accent-base"
                    >
                        Switch user
                    </Button>
                </Text>
            )}
        </header>
    );
}

// ─── ApplicationBanner ───────────────────────────────────────────────────────

/**
 * One line saying whose turn it is.
 *
 * `scenarioFromServer` has always computed this and the shell never
 * rendered it, so the hub's most-asked question — "am I waiting on you, or
 * are you waiting on me?" — was answered only by reading every row's badge.
 * The declined case keeps its own alert below; this line is the neutral
 * status the other states share.
 */
function ApplicationBanner({
    banner,
    celebrating
}: {
    banner: OnboardingScenario["banner"];
    celebrating: boolean;
}): ReactElement | null {
    if (banner.kind === "action_required") return null;
    if (banner.kind === "approved") {
        // While the celebration is up it says the same thing with more
        // ceremony, and owns the way into the console.
        if (celebrating) return null;
        // Every later visit to an approved application: the celebration is
        // spent, every row reads Complete, and this route renders full-bleed
        // with no sidebar or top bar. Without this the page is a dead end —
        // a finished checklist and no way off it.
        return <ApprovedBanner headline={banner.headline} />;
    }

    const tone =
        banner.kind === "your_turn"
            ? "text-foreground-base"
            : "text-foreground-muted";

    return (
        <section className="pt-5">
            <Text
                variant="small"
                className={`block leading-relaxed ${tone}`}
                role="status"
            >
                {banner.headline}
            </Text>
        </section>
    );
}

/**
 * The approved state's standing line: what happened, and the way into the
 * console beside it.
 *
 * The button is withheld while `clientConsoleAccess` is off, for the reason
 * the celebration card withholds its own: there is nothing to route to yet.
 * The line then reads as a hold rather than an invitation.
 */
function ApprovedBanner({ headline }: { headline: string }): ReactElement {
    const navigate = useNavigate();
    const { consoleAvailable } = useClientPlatformAccess();
    const consoleHeld = !consoleAvailable;

    return (
        <section className="pt-5 flex flex-wrap items-center justify-between gap-3">
            <Text
                variant="small"
                className="block leading-relaxed text-foreground-base"
                role="status"
            >
                {consoleHeld
                    ? "Your application is approved. Console access is opening soon."
                    : headline}
            </Text>
            {consoleHeld ? null : (
                <Button
                    variant="raised"
                    interaction="lifted"
                    size="sm"
                    onClick={() => navigate(CLIENT_HOME_PATH)}
                >
                    Go to console
                </Button>
            )}
        </section>
    );
}

// ─── CelebrationCard ─────────────────────────────────────────────────────────

const REDIRECT_NOTE =
    "You'll be automatically taken to Solomon Platform. Click here if you want to be taken right away.";

/**
 * Renders the post-approval card. Two layouts:
 *   - `page`  (default) — full-bleed centered for legacy / fallback callers
 *   - `panel` — fits inside the right-side aside in the hub layout, used
 *               by the staggered approval animation orchestrated from
 *               OnboardingHub. The auto-redirect fires either way.
 *
 * Composition: thin wrapper around `ResultPanel` that adds the panel-
 * layout title row + auto-redirect timer + scenario-specific copy. The
 * card visual + animation orchestration live on ResultPanel.
 *
 * While `FEATURE_FLAGS.clientConsoleAccess` is off the card holds instead:
 * the animation and the lite flow's acknowledgement are unchanged, but the
 * redirect timer never arms and the CTA is dropped, so the client reads
 * "access is opening soon" and stays on onboarding. See OnboardingGate.
 */
export function CelebrationCard({
    skipAnimation = false,
    layout = "page",
    autoRedirectMs = 3000,
    onContinue,
    title = "You've successfully onboarded with Solomon Labs",
    subtitle,
    subtitleSpacing,
    footnote
}: {
    skipAnimation?: boolean;
    layout?: "page" | "panel";
    /** Delay before auto-continuing after the celebration. Pass `null` to
     *  disable the timer entirely — the lite flow does this until the
     *  approval-acknowledgement POST has succeeded, so nothing routes before
     *  the gate would allow it. */
    autoRedirectMs?: number | null;
    /** Where "continue" goes, for BOTH the auto-redirect timer and the CTA
     *  button. Callers that must defer navigation until async work finishes
     *  (the lite flow waits for the ack POST, then patches the cache) pass
     *  this and own the routing. Omitted → default to `/positions`. */
    onContinue?: () => void;
    title?: string;
    subtitle?: string;
    subtitleSpacing?: "default" | "relaxed";
    footnote?: string;
}): ReactElement {
    const navigate = useNavigate();
    // Use the same authenticated release decision as the entry gate.
    const { consoleAvailable } = useClientPlatformAccess();
    const consoleHeld = !consoleAvailable;

    // Single navigation path shared by the timer and the CTA so neither can
    // route independently of the consumer's gating. Stable identity (depends
    // only on stable inputs) so the timer effect isn't reset every render.
    const goToDashboard = useCallback((): void => {
        if (onContinue) {
            onContinue();
            return;
        }
        navigate("/positions");
    }, [onContinue, navigate]);

    // Auto-route to the dashboard a moment after the animation finishes
    // so the celebration doesn't trap the user. The button below is a
    // manual fallback for anyone who reads slowly or hits a flaky
    // rerender. Animation runs ~1.4s; we hold the rest for breathing
    // room before the auto-transition. `null` disables the timer.
    useEffect(() => {
        if (autoRedirectMs === null || consoleHeld) return;
        const handle = window.setTimeout(() => {
            goToDashboard();
        }, autoRedirectMs);
        return (): void => window.clearTimeout(handle);
    }, [goToDashboard, autoRedirectMs, consoleHeld]);

    // The panel-layout has two stacked elements:
    //   1. The "Your Application is Approved" title row (with bottom
    //      border) — frames the celebration as the right-pane's content.
    //   2. The ResultPanel itself — the icon + title + subtitle + footnote
    //      + CTA card.
    // The outer wrapper provides the legacy section's `pt-4` breathing
    // room above the title. ResultPanel deliberately omits its own top
    // padding so the consumer controls vertical spacing without double-
    // padding (see ResultPanel JSDoc).
    const wrapperClass = layout === "panel" ? "block w-full pt-4" : "mt-25";

    // While the console is open, say that the redirect is coming and that the
    // button skips the wait. It takes the subtitle slot, or the footnote when
    // the caller already has a subtitle of its own (the minting-only copy).
    const redirectNote = consoleHeld ? undefined : REDIRECT_NOTE;
    const resolvedSubtitle = subtitle ?? redirectNote;
    const resolvedFootnote =
        subtitle !== undefined ? (footnote ?? redirectNote) : footnote;

    return (
        <div className={wrapperClass}>
            {layout === "panel" && (
                // Title + divider header to mirror the SubsectionDetailPanel
                // anatomy elsewhere in the hub. Keeps the right column
                // visually consistent across states. Sits OUTSIDE
                // ResultPanel because the "your application is approved"
                // headline is a wrapper concern (it frames the result),
                // not part of the result-card shape itself.
                <Heading
                    title="Your Application is Approved"
                    className="pb-4 border-b border-line-base"
                />
            )}
            <ResultPanel
                tone="success"
                icon={<CheckIcon className="h-10 w-10 text-foreground-base" />}
                /* TODO: replace "Solomon Labs" with `me.org.issuer.name`
                   once the /me response includes the client's issuer
                   relationship — same TODO as Layout.tsx. */
                title={title}
                subtitle={resolvedSubtitle}
                subtitleSpacing={subtitleSpacing}
                footnote={resolvedFootnote}
                cta={
                    consoleHeld ? undefined : (
                        <Button
                            variant="raised"
                            interaction="lifted"
                            size="md"
                            trailing={<ArrowRightLongIcon className="size-5" />}
                            onClick={goToDashboard}
                        >
                            Go to Console
                        </Button>
                    )
                }
                layout={layout}
                skipAnimation={skipAnimation}
                // Panel layout runs concurrently with the left-side
                // checkmark stagger; pacing it ~2x slower keeps the two
                // sequences feeling layered rather than racing. Page
                // layout (legacy/full-bleed) keeps its original tempo
                // since it isn't competing with anything else on screen.
                pacingScale={layout === "panel" ? 2 : 1}
            />
        </div>
    );
}
